import React, { useState, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import { Container, Link } from "@material-ui/core";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import MainAppBar from "../containers/MainAppBar";
import resultComponent from "./search/SearchResult";
import SearchService, { ResultList } from "../api/SearchService";
import SearchBar, { State } from "../containers/SearchBar";
import exportUtils, { sortListElem } from "../common/Utils";
import UserService from "../api/UserService";
import LoadingComponent from "../components/LoadingComponent";

const HOME_FONT_FAMILY =
  '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: 0,
      maxWidth: "100%",
      minWidth: "600px",
      maxHeight: "100%",
      minHeight: "950px",
    },
    homeContent: {
      color: "#1F2733",
      fontFamily: HOME_FONT_FAMILY,
      "& .MuiTypography-root": {
        fontFamily: HOME_FONT_FAMILY,
        letterSpacing: 0,
      },
      "& .MuiInputBase-input": {
        fontFamily: HOME_FONT_FAMILY,
        letterSpacing: 0,
      },
      "& .MuiButton-label": {
        fontFamily: HOME_FONT_FAMILY,
        letterSpacing: 0,
      },
      "& .MuiFormControlLabel-label": {
        fontFamily: HOME_FONT_FAMILY,
        letterSpacing: 0,
      },
    },
    contentContainer: {
      lineHeight: "20px",
      textAlign: "center",
      position: "relative",
      width: "1000px",
      minHeight: "60px",
      marginTop: "20px",
    },
    copyrightControl: {
      marginTop: "10px",
    },
    copyrightText: {
      color: "#7A8494",
      fontSize: "13px",
      fontWeight: 400,
      lineHeight: "22px",
    },
    outerContainer: {
      display: "flex",
      justifyContent: "center", // 水平居中
      alignItems: "center",    // 垂直居中
      //minHeight: "100vh",      // 至少占满整个视口高度
      //padding: theme.spacing(3), // 添加内边距（可选）
    },
    iconcontentContainer: {
      display: "flex",
      //flexDirection: ({ isMobile }) => (isMobile ? "column" : "row"), // 响应式方向
      alignItems: "center", // 子元素垂直居中
      justifyContent: "center",
      maxWidth: 800,        // 限制内容最大宽度（可选）
      margin: theme.spacing(4), // 元素间距（Material-UI v5语法，v4需替换）
      marginTop: "80px",
    },
    homeTitle: {
      color: "#162234",
      fontSize: "44px",
      fontWeight: 600,
      lineHeight: 1.25,
      marginBottom: theme.spacing(1),
    },
    homeSubtitle: {
      color: "#536173",
      fontSize: "23px",
      fontWeight: 400,
      lineHeight: 1.35,
      marginBottom: theme.spacing(1),
    },
    introSection: {
      width: "1000px",
      maxWidth: "calc(100% - 160px)",
      margin: "42px auto 34px",
      paddingTop: theme.spacing(3),
      borderTop: "1px solid #E1E7EF",
      [theme.breakpoints.down("sm")]: {
        maxWidth: "calc(100% - 48px)",
      },
    },
    introTitleRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(2),
    },
    introMarker: {
      width: "8px",
      height: "28px",
      marginRight: theme.spacing(2),
      backgroundColor: "#063E8B",
    },
    introTitle: {
      color: "#1F2733",
      fontSize: "23px",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    introText: {
      color: "#3F4B5B",
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "31px",
      textAlign: "justify",
    },
    introLink: {
      fontSize: "16px",
      fontWeight: 500,
    },
  })
);

function searchResultCount(resultList: ResultList) {
  return (
    resultList.wordResultList.length +
    resultList.templateResultList.length +
    resultList.dataResultList.length +
    resultList.MGIDResultList.length
  );
}

const Homeicon: React.FC = () => {
  const isMobile = window.innerWidth < 600; // 简单响应式判断
  const classes = useStyles({ isMobile });

  return (
    <Box className={classes.outerContainer}>
      <Box className={classes.iconcontentContainer}>
        <Box>
          <Typography className={classes.homeTitle} align="center">
            材料基因组标准数据库
          </Typography>
          <Typography className={classes.homeSubtitle} paragraph align="center">
            Materials Genome Standard Database
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Copyright: React.FC = () => {
  const classes = useStyles();

  return (
    <Container className={classes.copyrightControl}>
      <Typography className={classes.copyrightText} align="center">
        <Link
          href="https://beian.miit.gov.cn/"
          target="_blank"
          color="textSecondary"
          className={classes.copyrightText}
        >
          沪交ICP备20210259
        </Link>
      </Typography>
      <Typography className={classes.copyrightText} align="center">
        {"© "}
        {new Date().getFullYear()} Materials Genome Standard Database, All
        rights reserved.
      </Typography>
    </Container>
  );
};

const PlatformIntro: React.FC = () => {
  const classes = useStyles();

  return (
    <Box className={classes.introSection}>
      <Box className={classes.introTitleRow}>
        <Box className={classes.introMarker} />
        <Typography className={classes.introTitle}>平台简介</Typography>
      </Box>
      <Typography className={classes.introText} paragraph>
        平台围绕
        <Link
          href="/files/CSTM材料基因工程数据通则-final.pdf"
          target="_blank"
          className={classes.introLink}
        >
          《材料基因工程数据通则》
        </Link>
        ，管理材料制备、表征、分析、计算模拟和机器学习等研发方法标准。相关标准通过
        <Link
          href="http://www.cstm.com.cn/channel/details/biaozhunzhixiudingliucheng"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.introLink}
        >
          CSTM 审核
        </Link>
        后，可在平台创建并共享。
      </Typography>
      <Typography className={classes.introText}>
        平台支持标准化词汇和模板创建、标准研发数据的创建、存储、发表与引用，并提供面向材料基因工程数据探索的应用数据组织能力。平台还为数据提供 MGID 唯一标识，支持长期解析和定位。
      </Typography>
    </Box>
  );
};

//TODO: different offset for different types
const Home: React.FC = () => {
  const classes = useStyles();
  const SearchResult = resultComponent.SearchResult;
  const [query, setQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLastCardVisible, setLastCardVisible] = useState(false);
  const [queryStart, setQueryStart] = useState(0);
  const [queryStartLast, setQueryStartLast] = useState(0);
  const [continueSearch, setContinueSearch] = useState(false);
  const [resultMergedList, setResultMergedList] = useState<sortListElem[]>([]);
  const [state, setState] = useState<State>({
    word: true,
    templete: false,
    studydata: false,
    MGID: false,
  });

  useEffect(() => {
    void (async () => {
      const queryType: string[] = exportUtils.getQueryTypeList(state);
      if (
        isLastCardVisible &&
        continueSearch &&
        queryStart !== queryStartLast
      ) {
        const searchList: ResultList = await SearchService.getQuery(
          query,
          queryType,
          queryStart,
          queryType.length !== 0 ? Math.ceil(10 / queryType.length) : 0
        );
        const listCount: number = searchResultCount(searchList);
        if (listCount > 0) {
          setContinueSearch(true);
          const newSearchMergedList = exportUtils.mergeSearchList(searchList);
          setResultMergedList((oldResultMergedList) => {
            return oldResultMergedList.concat(newSearchMergedList);
          });
        } else {
          setContinueSearch(false);
        }
        setQueryStartLast(queryStart);
        setQueryStart((oldStart) => {
          return oldStart + Math.ceil(10 / queryType.length);
        });
      }
    })();
  }, [
    isLastCardVisible,
    continueSearch,
    state,
    queryStart,
    queryStartLast,
    query,
  ]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setIsClicked(false);
    setContinueSearch(false);
  };

  const handleClick = async () => {
    const queryType: string[] = exportUtils.getQueryTypeList(state);
    const searchList: ResultList = await SearchService.getQuery(
      query,
      queryType,
      0,
      queryType.length !== 0 ? Math.ceil(10 / queryType.length) : 0
    );
    setResultMergedList(exportUtils.mergeSearchList(searchList));
    setIsClicked(true);
    setContinueSearch(true);
    setQueryStart(
      queryType.length !== 0 ? Math.ceil(10 / queryType.length) : 0
    );
    setQueryStartLast(0);
    const listCount: number = searchResultCount(searchList);
    if (listCount > 0) {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
      setContinueSearch(false);
    }
    const checkLogin = await UserService.isLoggedIn();
    setLoggedIn(checkLogin);
  };

  const handleClickQueryType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, [event.target.name]: event.target.checked });
    setIsClicked(false);
    setContinueSearch(false);
  };

  return (
    <Container className={classes.root}>
      <MainAppBar />

      <Box className={classes.homeContent}>
        <Homeicon />

        <SearchBar
          query={query}
          state={state}
          handleClick={handleClick}
          handleChange={handleChange}
          handleClickQueryType={handleClickQueryType}
        />
        <Container className={classes.contentContainer}>
          <SearchResult
            resultMergedList={resultMergedList}
            isLoaded={isLoaded}
            isClicked={isClicked}
            isAuthed={loggedIn}
            query={query}
            setLastCardVisible={setLastCardVisible}
          />
        </Container>
        {queryStart !== queryStartLast && continueSearch ? (
          <LoadingComponent />
        ) : null}

        <PlatformIntro />

        <Box display="flex" alignItems="flex-end" justifyContent="center" p={1}>
          <Box p={1}>
            <Copyright />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Home;
